import React, {useEffect, useRef, useState} from 'react';
import {doc, serverTimestamp, setDoc} from 'firebase/firestore';
import {db} from '../Security/firebase-config';
import {resourceViewKey} from '../utils/resourceReview';

export default function useResourceVisit({uid, kind, teamId, mapId = '', resourceId, enabled, firestore = db}) {
  const lastSaved = useRef('');
  useEffect(() => {
    if (!enabled || !uid || !teamId || !resourceId || !firestore) return undefined;
    const key = resourceViewKey(kind, teamId, mapId, resourceId), identity = `${uid}/${key}`;
    if (lastSaved.current === identity) return undefined;
    let active = true;
    const save = () => {
      if (!active || lastSaved.current === identity) return;
      // Never mark notes as reviewed merely because the player was opened.
      void setDoc(doc(firestore, 'users', uid, 'resourceViews', key), {
        version: 1, kind, teamId, mapId, resourceId, viewedAt: serverTimestamp(),
      }).then(() => {if (active) lastSaved.current = identity;}).catch(() => {
        // Keep playback available offline; retry when connectivity returns.
      });
    };
    save();
    window.addEventListener('online', save);
    return () => {active = false; window.removeEventListener('online', save);};
  }, [uid, kind, teamId, mapId, resourceId, enabled, firestore]);
}

// The marker observes the actual card, not the entire loaded Stats collection.
export function ResourceViewMarker({uid, teamId, resourceId, firestore = db}) {
  const marker = useRef(null), [visibility, setVisibility] = useState(null);
  const identity = JSON.stringify([uid, teamId, resourceId]);
  useEffect(() => {
    const card = marker.current?.parentElement;
    if (!card || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(entries => {
      setVisibility({identity, visible: entries.some(entry => entry.isIntersecting && entry.intersectionRect.height >= Math.min(120, entry.boundingClientRect.height))});
    }, {threshold: [0, .1, .25, .5, 1]});
    observer.observe(card);
    return () => observer.disconnect();
  }, [identity]);
  useResourceVisit({uid, kind: 'stats', teamId, resourceId, enabled: visibility?.identity === identity && visibility.visible, firestore});
  // A hidden marker has no effect on the existing card's layout.
  return <span ref={marker} hidden aria-hidden="true"/>;
}
